import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useFarm } from '../../contexts/FarmContext';
import { feedbackAPI } from '../../services/api';
import toast from 'react-hot-toast';

// Only show feedback button on these routes
const ALLOWED_ROUTES = ['/dashboard', '/farm'];

const FeedbackButton = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { currentFarm } = useFarm();
  
  // Check if current route is allowed
  const isAllowedRoute = ALLOWED_ROUTES.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  );
  
  // Draggable state
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);

  // Initialize position on mount (bottom-right corner)
  useEffect(() => {
    const savedPosition = localStorage.getItem('feedbackButtonPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        // Validate position is within viewport
        const maxX = window.innerWidth - 60;
        const maxY = window.innerHeight - 60;
        setPosition({
          x: Math.min(Math.max(10, parsed.x), maxX),
          y: Math.min(Math.max(10, parsed.y), maxY)
        });
      } catch {
        setPosition({ x: window.innerWidth - 60, y: window.innerHeight - 100 });
      }
    } else {
      setPosition({ x: window.innerWidth - 60, y: window.innerHeight - 100 });
    }
  }, []);

  // Save position to localStorage when it changes
  useEffect(() => {
    if (position.x !== null && position.y !== null) {
      localStorage.setItem('feedbackButtonPosition', JSON.stringify(position));
    }
  }, [position]);

  // Handle drag start
  const handleDragStart = (e) => {
    e.preventDefault();
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    
    setIsDragging(true);
    setDragStart({
      x: clientX - position.x,
      y: clientY - position.y
    });
  };

  // Handle drag move
  useEffect(() => {
    const handleMove = (e) => {
      if (!isDragging) return;
      
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      
      const newX = clientX - dragStart.x;
      const newY = clientY - dragStart.y;
      
      // Keep within viewport bounds
      const maxX = window.innerWidth - 50;
      const maxY = window.innerHeight - 50;
      
      setPosition({
        x: Math.min(Math.max(10, newX), maxX),
        y: Math.min(Math.max(10, newY), maxY)
      });
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragStart]);

  // Handle click (only if not dragged)
  const handleClick = () => {
    if (!isDragging) {
      setOpen(true);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    
    if (!message.trim() || message.trim().length < 5) {
      toast.error('Please enter at least 5 characters of feedback');
      return;
    }
    
    try {
      setSubmitting(true);
      await feedbackAPI.submit({
        message: message.trim(),
        category: category || undefined,
        farmId: currentFarm?.id
      });
      toast.success('Thank you! Your feedback has been submitted.');
      setOpen(false);
      setMessage('');
      setCategory('');
    } catch (err) {
      console.error('Feedback submission error:', err);
      toast.error(err.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Don't render if not on allowed route or position not initialized
  if (!isAllowedRoute || position.x === null) return null;

  return (
    <>
      {/* Draggable Floating Button */}
      <button 
        ref={buttonRef}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onClick={handleClick}
        aria-label="Send feedback" 
        className={`fixed z-[45] rounded-full bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 ${isDragging ? 'scale-110 cursor-grabbing shadow-xl' : 'cursor-grab hover:scale-105'}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          touchAction: 'none'
        }}
        title="Send Feedback (drag to move)"
      >
        <ChatBubbleLeftRightIcon className="h-5 w-5"/>
      </button>

      {/* Feedback Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/30 flex items-end sm:items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md border border-gray-200 dark:border-gray-800">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Send Feedback</h3>
              <button onClick={()=>setOpen(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5"/></button>
            </div>
            <form onSubmit={submit} className="p-4 space-y-3">
              <div>
                <label className="label">Category (optional)</label>
                <select value={category} onChange={(e)=>setCategory(e.target.value)} className="input">
                  <option value="">General</option>
                  <option value="ui">UI/Design</option>
                  <option value="billing">Billing</option>
                  <option value="records">Records</option>
                  <option value="activities">Activities</option>
                  <option value="ai">AI Assistant</option>
                </select>
              </div>
              <div>
                <label className="label">Your feedback *</label>
                <textarea value={message} onChange={(e)=>setMessage(e.target.value)} rows={5} className="input" placeholder="Tell us what to improve..."/>
                <p className="text-xs text-gray-500 mt-1">Minimum 5 characters ({message.trim().length}/5)</p>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={()=>setOpen(false)} disabled={submitting} className="btn-outline">Cancel</button>
                <button 
                  type="submit" 
                  disabled={submitting || !message.trim() || message.trim().length < 5}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
